import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Plus, Trash2, Printer, RefreshCw, Search, Receipt, X } from 'lucide-react';
import { API } from '../App';

const today = () => new Date().toISOString().slice(0, 10);

const emptyQuoteItem = () => ({
  id: `${Date.now()}-${Math.random()}`,
  source: 'catalog',
  product_id: '',
  product_name: '',
  description: '',
  category: '',
  supplier_id: '',
  supplier_name: '',
  quantity: 1,
  unit: 'unidad',
  unit_price: 0,
  request_id: '',
  request_number: '',
  requester_id: '',
  requester_name: '',
  request_item_name: ''
});

const money = (n) => `Bs ${Number(n || 0).toLocaleString('es-BO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const esc = (v) => String(v ?? '').replace(/[&<>'"]/g, c => ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', "'":'&#39;', '"':'&quot;' }[c]));

export default function PurchaseQuote() {
  const [productSearches, setProductSearches] = useState({});
  const [productSearchOpen, setProductSearchOpen] = useState({});

  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [requestSearch, setRequestSearch] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [quote, setQuote] = useState({
    date: today(),
    client: '',
    observations: '',
    items: [emptyQuoteItem()]
  });

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [pr, s, p, r] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/suppliers`),
        axios.get(`${API}/purchases`),
        axios.get(`${API}/material-requests`).catch(() => ({ data: [] }))
      ]);
      setProducts(pr.data || []);
      setSuppliers(s.data || []);
      setPurchases(p.data || []);
      setRequests(r.data || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudieron cargar los datos del cotizador');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const orderOptions = useMemo(() => {
    const result = [];
    (requests || []).forEach((request) => {
      const number = request.order_number || request.request_number || `PED-${String(request.id || '').slice(0, 8).toUpperCase()}`;
      const items = Array.isArray(request.items) && request.items.length
        ? request.items
        : [{
            item_name: request.item_name || '',
            quantity: request.quantity || 1,
            unit: request.unit || 'unidad',
            request_type: request.request_type || '',
            notes: request.notes || ''
          }];
      items.forEach((item, index) => {
        result.push({
          key: `${request.id}-${index}`,
          request_id: request.id,
          request_number: number,
          requester_id: request.requester_id || '',
          requester_name: request.requester_name || 'Sin solicitante',
          status: request.status || '',
          priority: request.priority || '',
          item_name: item.item_name || '',
          quantity: Number(item.quantity_requested ?? item.quantity ?? 1),
          unit: item.unit || 'unidad',
          notes: item.notes || '',
          request_type: item.request_type || request.request_type || ''
        });
      });
    });
    return result;
  }, [requests]);

  const filteredOrders = useMemo(() => {
    const term = requestSearch.trim().toLowerCase();
    if (!term) return orderOptions;
    return orderOptions.filter(o => `${o.request_number} ${o.requester_name} ${o.item_name} ${o.request_type}`.toLowerCase().includes(term));
  }, [orderOptions, requestSearch]);

  const filteredProducts = useMemo(() => {
    const term = productSearch.trim().toLowerCase();
    if (!term) return products;
    return products.filter(p => `${p.name || ''} ${p.category || ''} ${p.brand || ''} ${p.part_number || ''}`.toLowerCase().includes(term));
  }, [products, productSearch]);

  const historyFor = (productId) => {
    const rows = [];
    (purchases || []).forEach(p => {
      (p.items || []).forEach(item => {
        if (String(item.product_id || '') === String(productId || '')) {
          const price = Number(item.unit_price || 0);
          if (price > 0) rows.push({ price, date: p.purchase_date || '', supplier_name: p.supplier_name || '' });
        }
      });
    });
    rows.sort((a, b) => String(b.date).localeCompare(String(a.date)));
    const prices = rows.map(r => r.price);
    return {
      last: prices[0] || 0,
      average: prices.length ? prices.reduce((a, b) => a + b, 0) / prices.length : 0,
      min: prices.length ? Math.min(...prices) : 0,
      latestSupplier: rows[0]?.supplier_name || '',
      count: prices.length
    };
  };

  const addItem = () => setQuote(q => ({ ...q, items: [...q.items, emptyQuoteItem()] }));
  const removeItem = (id) => setQuote(q => ({ ...q, items: q.items.filter(i => i.id !== id) }));

  const updateItem = (id, patch) => {
    setQuote(q => ({ ...q, items: q.items.map(i => i.id === id ? { ...i, ...patch } : i) }));
  };

  const applyOrder = (id, orderKey) => {
    const order = orderOptions.find(o => o.key === orderKey);
    if (!order) {
      updateItem(id, { request_id: '', request_number: '', requester_id: '', requester_name: '', request_item_name: '' });
      return;
    }
    const matchingProduct = products.find(p => String(p.name || '').trim().toLowerCase() === String(order.item_name || '').trim().toLowerCase());
    const patch = {
      request_id: order.request_id,
      request_number: order.request_number,
      requester_id: order.requester_id,
      requester_name: order.requester_name,
      request_item_name: order.item_name,
      quantity: order.quantity || 1,
      unit: order.unit || 'unidad'
    };
    if (matchingProduct) {
      const h = historyFor(matchingProduct.id);
      patch.source = 'catalog';
      patch.product_id = matchingProduct.id;
      patch.product_name = matchingProduct.name || order.item_name;
      patch.category = matchingProduct.category || '';
      patch.supplier_name = h.latestSupplier || '';
      patch.unit_price = h.last || 0;
    } else {
      patch.source = 'manual';
      patch.product_id = '';
      patch.product_name = order.item_name || '';
      patch.supplier_name = '';
      patch.unit_price = 0;
    }
    updateItem(id, patch);
  };

  const selectProduct = (id, productId) => {
    const p = products.find(x => String(x.id) === String(productId));
    if (!p) {
      updateItem(id, { product_id: '', product_name: '', category: '', supplier_id: '', supplier_name: '', unit_price: 0 });
      return;
    }
    const h = historyFor(p.id);
    const supplier = suppliers.find(s => String(s.name || '').trim().toLowerCase() === String(h.latestSupplier || '').trim().toLowerCase());
    updateItem(id, {
      source: 'catalog',
      product_id: p.id,
      product_name: p.name || '',
      description: p.description || '',
      category: p.category || '',
      unit: p.unit || 'unidad',
      supplier_id: supplier?.id || '',
      supplier_name: h.latestSupplier || '',
      unit_price: h.last || 0
    });
  };


  const getProductSearchResults = (itemId) => {
    const term = String(productSearches[itemId] || '').trim().toLowerCase();

    if (!term) return [];

    return products
      .filter(p => {
        const text = [
          p.name,
          p.brand,
          p.part_number,
          p.category
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return text.includes(term);
      })
      .slice(0, 8);
  };

  const openProductSearch = (item) => {
    setProductSearchOpen(prev => ({
      ...prev,
      [item.id]: true
    }));

    if (item.source === 'catalog' && item.product_name) {
      setProductSearches(prev => ({
        ...prev,
        [item.id]: item.product_name
      }));
    }
  };

  const closeProductSearch = (itemId) => {
    setTimeout(() => {
      setProductSearchOpen(prev => ({
        ...prev,
        [itemId]: false
      }));
    }, 180);
  };

  const handleProductSearch = (item, value) => {
    setProductSearches(prev => ({
      ...prev,
      [item.id]: value
    }));

    setProductSearchOpen(prev => ({
      ...prev,
      [item.id]: true
    }));

    // Si el usuario empieza a escribir algo diferente,
    // dejamos de considerar el producto anterior seleccionado.
    if (
      item.source === 'catalog' &&
      value !== item.product_name
    ) {
      updateItem(item.id, {
        source: 'manual',
        product_id: '',
        product_name: value,
        supplier_id: '',
        supplier_name: '',
        unit_price: 0
      });
    } else if (item.source === 'manual') {
      updateItem(item.id, {
        product_name: value
      });
    }
  };

  const chooseProduct = (item, product) => {
    selectProduct(item.id, product.id);

    setProductSearches(prev => ({
      ...prev,
      [item.id]: product.name || ''
    }));

    setProductSearchOpen(prev => ({
      ...prev,
      [item.id]: false
    }));
  };

  const chooseManualProduct = (item) => {
    const value = productSearches[item.id] || '';

    updateItem(item.id, {
      source: 'manual',
      product_id: '',
      product_name: value,
      supplier_id: '',
      supplier_name: '',
      unit_price: 0
    });

    setProductSearchOpen(prev => ({
      ...prev,
      [item.id]: false
    }));
  };

  const total = useMemo(() => quote.items.reduce((sum, i) => sum + Number(i.quantity || 0) * Number(i.unit_price || 0), 0), [quote.items]);

  const resetQuote = () => setQuote({ date: today(), client: '', observations: '', items: [emptyQuoteItem()] });

  const printQuote = () => {

    const number =
      `PF-${new Date().toISOString().replace(/[-:TZ.]/g, '').slice(0, 14)}`;

    const formatNumber = (value) =>
      Number(value || 0).toLocaleString('es-BO', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });

    const rows = quote.items.map((item, index) => {

      const quantity = Number(item.quantity || 0);
      const unitPrice = Number(item.unit_price || 0);
      const subtotal = quantity * unitPrice;

      const pedido =
        item.request_number ||
        'Sin pedido';

      const solicitado =
        item.requester_name ||
        '—';

      return `
        <tr>

          <td class="num">
            ${index + 1}
          </td>

          <td class="product">

            <div class="product-name">
              ${esc(item.product_name || 'Producto sin nombre')}
            </div>

            ${
              item.description
                ? `<div class="description">${esc(item.description)}</div>`
                : ''
            }

          </td>

          <td>
            <div class="supplier">
              ${esc(item.supplier_name || '—')}
            </div>
          </td>

          <td>

            <div class="order-number">
              ${esc(pedido)}
            </div>

            <div class="requester">
              ${esc(solicitado)}
            </div>

          </td>

          <td class="center">
            ${formatNumber(quantity)}
          </td>

          <td class="center">
            ${esc(item.unit || 'unidad')}
          </td>

          <td class="money">
            Bs ${formatNumber(unitPrice)}
          </td>

          <td class="money total-cell">
            Bs ${formatNumber(subtotal)}
          </td>

        </tr>
      `;
    }).join('');

    const html = `<!DOCTYPE html>

<html lang="es">

<head>

<meta charset="UTF-8">

<title>${number} - MAQCONTROL</title>

<style>

@page {
  size: A4 landscape;
  margin: 12mm;
}

* {
  box-sizing: border-box;
}

body {
  margin: 0;
  background: white;
  color: #172033;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 10px;
}

.page {
  width: 100%;
}

/* =========================
   HEADER
   ========================= */

.header {
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  border-bottom: 3px solid #f97316;
  padding-bottom: 12px;
}

.brand {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.brand-name {
  font-size: 30px;
  font-weight: 800;
  letter-spacing: -1px;
  color: #172033;
}

.brand-subtitle {
  margin-top: 2px;
  color: #64748b;
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 1.2px;
}

.document-title {
  margin-top: 12px;
  font-size: 18px;
  font-weight: 800;
  color: #f97316;
  text-transform: uppercase;
  letter-spacing: .8px;
}

.document-box {
  width: 220px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  overflow: hidden;
}

.document-box-title {
  background: #172033;
  color: white;
  padding: 7px 10px;
  font-size: 8px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.document-box-body {
  padding: 10px;
  background: #f8fafc;
}

.document-number {
  font-size: 13px;
  font-weight: 800;
}

.document-date {
  margin-top: 4px;
  color: #64748b;
  font-size: 9px;
}

/* =========================
   INFO
   ========================= */

.info {
  display: grid;
  grid-template-columns: 2fr .6fr 1fr;
  gap: 10px;
  margin-top: 14px;
}

.info-box {
  border: 1px solid #d6dee8;
  border-radius: 8px;
  padding: 10px 12px;
  background: #fff;
}

.info-label {
  color: #64748b;
  font-size: 8px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .6px;
}

.info-value {
  margin-top: 5px;
  font-size: 12px;
  font-weight: 700;
}

/* =========================
   TABLE TITLE
   ========================= */

.section-title {
  margin-top: 16px;
  margin-bottom: 6px;
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .7px;
  color: #172033;
}

/* =========================
   TABLE
   ========================= */

table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #cbd5e1;
}

thead {
  display: table-header-group;
}

thead th {
  background: #172033;
  color: white;
  padding: 9px 7px;
  font-size: 8px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .4px;
  text-align: left;
  border-right: 1px solid #3b4658;
}

thead th:last-child {
  border-right: none;
}

tbody tr {
  page-break-inside: avoid;
}

tbody tr:nth-child(even) {
  background: #f8fafc;
}

tbody td {
  padding: 9px 7px;
  border-top: 1px solid #dbe2ea;
  border-right: 1px solid #e2e8f0;
  vertical-align: middle;
  font-size: 9.5px;
}

tbody td:last-child {
  border-right: none;
}

.num {
  width: 32px;
  text-align: center;
  font-weight: 700;
  color: #64748b;
}

.product {
  min-width: 250px;
}

.product-name {
  font-weight: 800;
  font-size: 10px;
  color: #172033;
}

.description {
  margin-top: 3px;
  color: #64748b;
  font-size: 8px;
}

.supplier {
  font-weight: 600;
}

.order-number {
  font-weight: 700;
  font-size: 9px;
}

.requester {
  margin-top: 3px;
  color: #64748b;
  font-size: 8px;
}

.center {
  text-align: center;
}

.money {
  text-align: right;
  white-space: nowrap;
}

.total-cell {
  font-weight: 800;
  color: #172033;
}

/* =========================
   BOTTOM SUMMARY
   ========================= */

.bottom {
  display: grid;
  grid-template-columns: 1fr 270px;
  gap: 15px;
  margin-top: 12px;
  align-items: start;
}

.observations {
  border: 1px solid #d6dee8;
  border-left: 4px solid #f97316;
  border-radius: 7px;
  padding: 10px 12px;
  background: #f8fafc;
}

.observations-title {
  font-size: 8px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .6px;
  color: #475569;
  margin-bottom: 5px;
}

.observations-text {
  font-size: 9px;
  color: #334155;
}

.total-panel {
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #cbd5e1;
}

.total-panel-title {
  background: #172033;
  color: white;
  padding: 8px 11px;
  font-size: 9px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .7px;
}

.total-panel-value {
  background: #fff7ed;
  padding: 12px;
  color: #ea580c;
  font-size: 22px;
  font-weight: 900;
  text-align: right;
}

/* =========================
   CONDITIONS
   ========================= */

.conditions {
  margin-top: 10px;
  padding: 8px 10px;
  border: 1px dashed #cbd5e1;
  border-radius: 6px;
  color: #64748b;
  font-size: 8.5px;
}

.conditions strong {
  color: #334155;
}

/* =========================
   SIGNATURES
   ========================= */

.signatures {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 120px;
  margin: 28px 80px 0;
}

.signature {
  border-top: 1px solid #94a3b8;
  padding-top: 6px;
  text-align: center;
  color: #475569;
  font-size: 8.5px;
}

/* =========================
   FOOTER
   ========================= */

.footer {
  margin-top: 14px;
  padding-top: 7px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  color: #94a3b8;
  font-size: 7.5px;
}

@media print {

  body {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

}

</style>

</head>

<body>

<div class="page">

  <!-- ENCABEZADO -->

  <div class="header">

    <div class="brand">

      <div class="brand-name">
        MAQCONTROL
      </div>

      <div class="brand-subtitle">
        Sistema de Gestión · Compras
      </div>

      <div class="document-title">
        PROFORMA / PRESUPUESTO
      </div>

    </div>

    <div class="document-box">

      <div class="document-box-title">
        Documento
      </div>

      <div class="document-box-body">

        <div class="document-number">
          ${number}
        </div>

        <div class="document-date">
          Fecha: ${esc(quote.date)}
        </div>

      </div>

    </div>

  </div>


  <!-- INFORMACIÓN -->

  <div class="info">

    <div class="info-box">

      <div class="info-label">
        Cliente / Solicitante
      </div>

      <div class="info-value">
        ${esc(quote.client || '—')}
      </div>

    </div>

    <div class="info-box">

      <div class="info-label">
        Ítems
      </div>

      <div class="info-value">
        ${quote.items.length}
      </div>

    </div>

    <div class="info-box">

      <div class="info-label">
        Total
      </div>

      <div class="info-value">
        Bs ${formatNumber(total)}
      </div>

    </div>

  </div>


  <!-- DETALLE -->

  <div class="section-title">
    Detalle de productos
  </div>

  <table>

    <thead>

      <tr>

        <th style="width:32px;text-align:center">#</th>

        <th>Producto</th>

        <th style="width:145px">Proveedor</th>

        <th style="width:145px">Pedido / Solicitante</th>

        <th style="width:55px;text-align:center">Cant.</th>

        <th style="width:65px;text-align:center">Unidad</th>

        <th style="width:90px;text-align:right">P. Unit.</th>

        <th style="width:100px;text-align:right">Total</th>

      </tr>

    </thead>

    <tbody>

      ${rows}

    </tbody>

  </table>


  <!-- TOTAL + OBSERVACIONES -->

  <div class="bottom">

    <div class="observations">

      <div class="observations-title">
        Observaciones
      </div>

      <div class="observations-text">
        ${esc(quote.observations || 'Sin observaciones')}
      </div>

    </div>

    <div class="total-panel">

      <div class="total-panel-title">
        Total de la proforma
      </div>

      <div class="total-panel-value">
        Bs ${formatNumber(total)}
      </div>

    </div>

  </div>


  <div class="conditions">

    <strong>Condiciones:</strong>
    Precios referenciales sujetos a confirmación y disponibilidad del proveedor.
    Esta proforma constituye un presupuesto y no representa por sí misma una compra registrada.

  </div>


  <!-- FIRMAS -->

  <div class="signatures">

    <div class="signature">
      Responsable de cotización
    </div>

    <div class="signature">
      Cliente / Solicitante
    </div>

  </div>


  <!-- FOOTER -->

  <div class="footer">

    <span>
      Documento generado desde MaqControl · Cotizador
    </span>

    <span>
      ${number}
    </span>

  </div>

</div>


<script>

window.onload = function() {

  setTimeout(function() {
    window.print();
  }, 350);

};

</script>

</body>

</html>`;

    const printWindow = window.open(
      '',
      '_blank',
      'width=1100,height=800'
    );

    if (!printWindow) {

      alert(
        'El navegador bloqueó la ventana de impresión. ' +
        'Permite ventanas emergentes para MaqControl.'
      );

      return;
    }

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  };

  return (
    <div className="cotizador-v2 w-full space-y-4 sm:space-y-5">

      <style>{`

        /* ===== COTIZADOR V2.1 - COMPACTO ===== */


        /* ===== COTIZADOR V2.1 - COMPACTO ===== */

        .cotizador-v2 {
          width: 100%;
          max-width: none !important;
          margin: 0 !important;
        }

        .cotizador-v2 > * {
          max-width: none !important;
        }

        .cotizador-v2 .quote-card {
          border-radius: 14px;
        }

        .cotizador-v2 .quote-card > .p-4 {
          padding: 14px;
        }

        .cotizador-v2 .product-layout {
          grid-template-columns: minmax(0, 1.35fr) minmax(360px, .95fr);
          gap: 14px;
        }

        .cotizador-v2 .search-results {
          max-height: 280px;
        }

        .cotizador-v2 .history-compact {
          font-size: 11px;
          line-height: 1.3;
        }

        .cotizador-v2 .history-compact .history-values {
          display: flex;
          flex-wrap: wrap;
          gap: 6px 12px;
          align-items: center;
        }

        .cotizador-v2 .history-compact .history-values span {
          white-space: nowrap;
        }

        .cotizador-v2 .economic-grid {
          display: grid;
          grid-template-columns: minmax(150px, 1.5fr) 90px 105px minmax(120px, 1fr);
          gap: 10px;
          align-items: end;
        }

        .cotizador-v2 .economic-field label {
          margin-bottom: 4px;
        }

        .cotizador-v2 .economic-field input,
        .cotizador-v2 .economic-field select {
          min-height: 40px;
          padding-top: 8px;
          padding-bottom: 8px;
        }

        .cotizador-v2 .compact-subtotal {
          min-height: 40px;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          white-space: nowrap;
        }

        .cotizador-v2 .sticky-total-inner {
          padding: 12px 16px;
        }

        @media (min-width: 1024px) {
          .cotizador-v2 {
            padding-left: 2px;
            padding-right: 2px;
          }

          .cotizador-v2 .quote-card .product-layout {
            grid-template-columns: minmax(0, 1.25fr) minmax(420px, 1fr);
          }

          .cotizador-v2 .quote-card {
            width: 100%;
          }

          .cotizador-v2 .quote-card > .p-4 {
            padding: 12px 14px;
          }

          .cotizador-v2 .quote-card .space-y-4 > * {
            margin-top: 10px;
          }

          .cotizador-v2 .quote-card .space-y-4 > *:first-child {
            margin-top: 0;
          }
        }

        @media (max-width: 900px) {
          .cotizador-v2 .product-layout {
            grid-template-columns: 1fr;
          }

          .cotizador-v2 .economic-grid {
            grid-template-columns: 1.5fr .7fr .9fr;
          }

          .cotizador-v2 .economic-field.subtotal-field {
            grid-column: 1 / -1;
          }
        }

        @media (max-width: 640px) {

          .cotizador-v2 {
            padding-bottom: 92px;
          }

          .cotizador-v2 .quote-card > .p-4 {
            padding: 12px;
          }

          .cotizador-v2 .product-layout {
            display: block;
          }

          .cotizador-v2 .economic-grid {
            grid-template-columns: 1fr 1fr;
            gap: 10px;
          }

          .cotizador-v2 .economic-field:first-child {
            grid-column: 1 / -1;
          }

          .cotizador-v2 .economic-field.subtotal-field {
            grid-column: 1 / -1;
          }

          .cotizador-v2 .compact-subtotal {
            justify-content: space-between;
            border-top: 1px solid #e2e8f0;
            padding-top: 10px;
            margin-top: 2px;
          }

          .cotizador-v2 .history-compact {
            padding: 8px 10px !important;
            margin-top: 6px !important;
          }

          .cotizador-v2 .history-values {
            gap: 4px 8px !important;
          }

          .cotizador-v2 .quote-card .border.rounded-xl.p-3 {
            padding: 10px;
          }
        }

        .cotizador-v2 {
          width: 100%;
          max-width: none !important;
          margin: 0 !important;
        }

        .cotizador-v2 > * {
          max-width: none !important;
        }

        .cotizador-v2 .quote-card {
          border-radius: 14px;
        }

        .cotizador-v2 .quote-card > .p-4 {
          padding: 14px;
        }

        .cotizador-v2 .product-layout {
          grid-template-columns: minmax(0, 1.35fr) minmax(360px, .95fr);
          gap: 14px;
        }

        .cotizador-v2 .search-results {
          max-height: 280px;
        }

        .cotizador-v2 .history-compact {
          font-size: 11px;
          line-height: 1.3;
        }

        .cotizador-v2 .history-compact .history-values {
          display: flex;
          flex-wrap: wrap;
          gap: 6px 12px;
          align-items: center;
        }

        .cotizador-v2 .history-compact .history-values span {
          white-space: nowrap;
        }

        .cotizador-v2 .economic-grid {
          display: grid;
          grid-template-columns: minmax(150px, 1.5fr) 90px 105px minmax(120px, 1fr);
          gap: 10px;
          align-items: end;
        }

        .cotizador-v2 .economic-field label {
          margin-bottom: 4px;
        }

        .cotizador-v2 .economic-field input,
        .cotizador-v2 .economic-field select {
          min-height: 40px;
          padding-top: 8px;
          padding-bottom: 8px;
        }

        .cotizador-v2 .compact-subtotal {
          min-height: 40px;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          white-space: nowrap;
        }

        .cotizador-v2 .sticky-total-inner {
          padding: 12px 16px;
        }

        @media (min-width: 1024px) {
          .cotizador-v2 {
            padding-left: 2px;
            padding-right: 2px;
          }

          .cotizador-v2 .quote-card .product-layout {
            grid-template-columns: minmax(0, 1.25fr) minmax(420px, 1fr);
          }

          .cotizador-v2 .quote-card {
            width: 100%;
          }

          .cotizador-v2 .quote-card > .p-4 {
            padding: 12px 14px;
          }

          .cotizador-v2 .quote-card .space-y-4 > * {
            margin-top: 10px;
          }

          .cotizador-v2 .quote-card .space-y-4 > *:first-child {
            margin-top: 0;
          }
        }

        @media (max-width: 900px) {
          .cotizador-v2 .product-layout {
            grid-template-columns: 1fr;
          }

          .cotizador-v2 .economic-grid {
            grid-template-columns: 1.5fr .7fr .9fr;
          }

          .cotizador-v2 .economic-field.subtotal-field {
            grid-column: 1 / -1;
          }
        }

        @media (max-width: 640px) {

          .cotizador-v2 {
            padding-bottom: 92px;
          }

          .cotizador-v2 .quote-card > .p-4 {
            padding: 12px;
          }

          .cotizador-v2 .product-layout {
            display: block;
          }

          .cotizador-v2 .economic-grid {
            grid-template-columns: 1fr 1fr;
            gap: 10px;
          }

          .cotizador-v2 .economic-field:first-child {
            grid-column: 1 / -1;
          }

          .cotizador-v2 .economic-field.subtotal-field {
            grid-column: 1 / -1;
          }

          .cotizador-v2 .compact-subtotal {
            justify-content: space-between;
            border-top: 1px solid #e2e8f0;
            padding-top: 10px;
            margin-top: 2px;
          }

          .cotizador-v2 .history-compact {
            padding: 8px 10px !important;
            margin-top: 6px !important;
          }

          .cotizador-v2 .history-values {
            gap: 4px 8px !important;
          }

          .cotizador-v2 .quote-card .border.rounded-xl.p-3 {
            padding: 10px;
          }
        }

        .cotizador-v2 {
          color: #172033;
        }

        .cotizador-v2 input,
        .cotizador-v2 select,
        .cotizador-v2 textarea {
          min-height: 44px;
          font-size: 16px;
        }

        .cotizador-v2 button {
          min-height: 44px;
        }

        .cotizador-v2 .quote-card {
          transition: box-shadow .15s ease, transform .15s ease;
        }

        .cotizador-v2 .quote-card:hover {
          box-shadow: 0 8px 25px rgba(15, 23, 42, .08);
        }

        .cotizador-v2 .search-results {
          max-height: 330px;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
        }

        .cotizador-v2 .sticky-total {
          position: sticky;
          bottom: 12px;
          z-index: 30;
        }

        @media (max-width: 640px) {
          .cotizador-v2 {
            padding-bottom: 90px;
          }

          .cotizador-v2 .sticky-total {
            position: fixed;
            left: 10px;
            right: 10px;
            bottom: 10px;
            margin: 0;
          }

          .cotizador-v2 .sticky-total-inner {
            border-radius: 16px;
            box-shadow: 0 10px 35px rgba(15, 23, 42, .20);
          }

          .cotizador-v2 .quote-card {
            border-radius: 16px;
          }

          .cotizador-v2 .field-grid {
            grid-template-columns: 1fr 1fr;
          }

          .cotizador-v2 .field-full {
            grid-column: 1 / -1;
          }

          .cotizador-v2 .action-grid {
            grid-template-columns: 1fr 1fr;
          }

          .cotizador-v2 .product-name {
            font-size: 1rem;
            line-height: 1.35;
          }
        }

        @media (min-width: 641px) {
          .cotizador-v2 .product-layout {
            display: grid;
            grid-template-columns: minmax(0, 1.45fr) minmax(280px, .75fr);
            gap: 18px;
            align-items: start;
          }
        }
      `}</style>

      {/* CABECERA */}
      <div className="bg-white border rounded-2xl overflow-hidden">

        <div className="p-3 sm:p-4 border-b bg-slate-50/70">

          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">

            <div>
              <div className="flex items-center gap-2">
                <span className="text-xl">🧾</span>
                <h1 className="text-xl sm:text-2xl font-bold text-slate-800">
                  Cotizador
                </h1>
              </div>

              <p className="text-sm text-slate-500 mt-1">
                Crea una proforma utilizando productos, pedidos y precios históricos.
              </p>
            </div>

            <div className="grid grid-cols-2 sm:flex gap-2">

              <button
                type="button"
                onClick={resetQuote}
                className="px-3 py-2.5 rounded-xl border bg-white hover:bg-slate-50 font-medium"
              >
                ↻ Nueva
              </button>

              <button
                type="button"
                onClick={printQuote}
                className="px-4 py-2.5 rounded-xl bg-slate-800 text-white hover:bg-slate-900 font-medium"
              >
                🖨 Imprimir / PDF
              </button>

            </div>
          </div>

        </div>

        {/* DATOS GENERALES */}
        <div className="p-3 sm:p-4">

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
                Fecha
              </label>

              <input
                type="date"
                value={quote.date}
                onChange={e =>
                  setQuote(q => ({
                    ...q,
                    date: e.target.value
                  }))
                }
                className="w-full border rounded-xl px-3 py-2.5 bg-white"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
                Cliente / solicitante
              </label>

              <input
                value={quote.client}
                onChange={e =>
                  setQuote(q => ({
                    ...q,
                    client: e.target.value
                  }))
                }
                placeholder="Nombre del cliente o área"
                className="w-full border rounded-xl px-3 py-2.5"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
                Observaciones
              </label>

              <input
                value={quote.observations}
                onChange={e =>
                  setQuote(q => ({
                    ...q,
                    observations: e.target.value
                  }))
                }
                placeholder="Destino, condiciones, etc."
                className="w-full border rounded-xl px-3 py-2.5"
              />
            </div>

          </div>

        </div>

      </div>

      {/* ENCABEZADO DE PRODUCTOS */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">

        <div>
          <h2 className="font-bold text-lg text-slate-800">
            Productos de la proforma
          </h2>

          <p className="text-xs sm:text-sm text-slate-500">
            {quote.items.length} {quote.items.length === 1 ? 'producto' : 'productos'} agregados
          </p>
        </div>

        <button
          type="button"
          onClick={addItem}
          className="w-full sm:w-auto px-4 py-3 rounded-xl bg-slate-800 text-white hover:bg-slate-900 font-semibold"
        >
          + Agregar producto
        </button>

      </div>

      {/* PRODUCTOS */}
      <div className="space-y-4">

        {quote.items.map((item, index) => {

          const h = item.product_id
            ? historyFor(item.product_id)
            : null;

          const subtotal =
            Number(item.quantity || 0) *
            Number(item.unit_price || 0);

          const selectedOrder =
            item.request_id
              ? orderOptions.find(
                  o =>
                    o.request_id === item.request_id &&
                    o.item_name === item.request_item_name
                )
              : null;

          return (
            <div
              key={item.id}
              className="quote-card bg-white border rounded-2xl overflow-visible"
            >

              {/* CABECERA TARJETA */}
              <div className="px-4 py-3 border-b bg-slate-50/70 flex items-center justify-between gap-3">

                <div className="flex items-center gap-3 min-w-0">

                  <div className="w-9 h-9 rounded-full bg-slate-800 text-white flex items-center justify-center font-bold shrink-0">
                    {index + 1}
                  </div>

                  <div className="min-w-0">
                    <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                      Producto
                    </div>

                    <div className="product-name font-semibold truncate">
                      {item.product_name || 'Nuevo producto'}
                    </div>
                  </div>

                </div>

                <button
                  type="button"
                  onClick={() => removeItem(item.id)}
                  disabled={quote.items.length === 1}
                  className="shrink-0 w-10 h-10 rounded-xl text-red-600 hover:bg-red-50 disabled:opacity-30"
                  title="Eliminar producto"
                >
                  🗑
                </button>

              </div>

              <div className="p-4">

                <div className="product-layout">

                  {/* COLUMNA PRINCIPAL */}
                  <div className="space-y-4">

                    {/* BUSCADOR */}
                    <div className="relative">

                      <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
                        Buscar producto
                      </label>

                      <input
                        type="text"
                        value={
                          productSearches[item.id] ??
                          item.product_name ??
                          ''
                        }
                        onFocus={() => openProductSearch(item)}
                        onBlur={() => closeProductSearch(item.id)}
                        onChange={e =>
                          handleProductSearch(
                            item,
                            e.target.value
                          )
                        }
                        placeholder="🔍 Escriba nombre, marca o número de parte..."
                        className="w-full border-2 border-slate-200 focus:border-slate-500 rounded-xl px-3 py-3"
                        autoComplete="off"
                      />

                      {productSearchOpen[item.id] && (
                        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border rounded-xl shadow-2xl overflow-hidden">

                          <div className="search-results">

                            {getProductSearchResults(item.id).map(p => {

                              const ph = historyFor(p.id);

                              return (
                                <button
                                  key={p.id}
                                  type="button"
                                  onMouseDown={e => e.preventDefault()}
                                  onClick={() =>
                                    chooseProduct(item, p)
                                  }
                                  className="w-full text-left px-4 py-3.5 hover:bg-slate-50 border-b"
                                >

                                  <div className="font-semibold text-sm">
                                    📦 {p.name}
                                  </div>

                                  <div className="text-xs text-slate-500 mt-1">
                                    {p.brand ? `${p.brand} · ` : ''}
                                    {p.part_number ? `Parte: ${p.part_number} · ` : ''}
                                    {p.category || ''}
                                  </div>

                                  {ph?.count > 0 && (
                                    <div className="text-xs text-slate-500 mt-1">
                                      Último:
                                      <b className="text-slate-700 ml-1">
                                        {money(ph.last)}
                                      </b>
                                      {' · '}
                                      Prom.:
                                      <b className="text-slate-700 ml-1">
                                        {money(ph.average)}
                                      </b>
                                      {ph.latestSupplier && (
                                        <>
                                          {' · '}
                                          {ph.latestSupplier}
                                        </>
                                      )}
                                    </div>
                                  )}

                                </button>
                              );
                            })}

                            {String(productSearches[item.id] || '').trim() &&
                              getProductSearchResults(item.id).length === 0 && (
                                <div className="p-4 text-sm text-slate-500">
                                  No encontramos ese producto en el catálogo.
                                </div>
                              )}

                            {String(productSearches[item.id] || '').trim() && (
                              <button
                                type="button"
                                onMouseDown={e => e.preventDefault()}
                                onClick={() =>
                                  chooseManualProduct(item)
                                }
                                className="w-full text-left px-4 py-3.5 bg-slate-50 hover:bg-slate-100 font-semibold text-sm"
                              >
                                ✎ Usar como producto manual / no catalogado
                              </button>
                            )}

                          </div>

                        </div>
                      )}

                    </div>

                    {/* PEDIDO */}
                    <div className="border rounded-xl p-3 bg-slate-50/60">

                      <div className="flex items-center justify-between gap-2 mb-2">

                        <div>
                          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Pedido asociado
                          </div>

                          <div className="text-xs text-slate-500">
                            Opcional
                          </div>
                        </div>

                        {item.requester_name && (
                          <span className="text-xs font-semibold bg-white border rounded-full px-2.5 py-1">
                            👤 {item.requester_name}
                          </span>
                        )}

                      </div>

                      <select
                        value={
                          item.request_id
                            ? selectedOrder?.key || ''
                            : ''
                        }
                        onChange={e =>
                          applyOrder(
                            item.id,
                            e.target.value
                          )
                        }
                        className="w-full border rounded-xl px-3 py-2.5 bg-white"
                      >
                        <option value="">
                          Sin pedido asociado
                        </option>

                        {filteredOrders.map(o => (
                          <option
                            key={o.key}
                            value={o.key}
                          >
                            {o.request_number} · {o.requester_name} · {o.item_name}
                          </option>
                        ))}

                      </select>

                      {item.requester_name && (
                        <div className="mt-2 text-xs text-slate-500">
                          <b>Pedido:</b> {item.request_number}
                        </div>
                      )}

                    </div>

                  </div>

                  {/* COLUMNA DE DATOS ECONÓMICOS */}
                  <div className="space-y-4">

                    {/* PROVEEDOR */}
                    <div>

                      <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
                        Proveedor
                      </label>

                      {item.source === 'catalog' ? (

                        <select
                          value={item.supplier_id || ''}
                          onChange={e => {
                            const supplier =
                              suppliers.find(
                                x => x.id === e.target.value
                              );

                            updateItem(item.id, {
                              supplier_id: supplier?.id || '',
                              supplier_name: supplier?.name || ''
                            });
                          }}
                          className="w-full border rounded-xl px-3 py-2.5 bg-white"
                        >
                          <option value="">
                            Proveedor / referencia
                          </option>

                          {suppliers.map(s => (
                            <option
                              key={s.id}
                              value={s.id}
                            >
                              {s.name}
                            </option>
                          ))}

                        </select>

                      ) : (

                        <input
                          value={item.supplier_name || ''}
                          onChange={e =>
                            updateItem(
                              item.id,
                              {
                                supplier_name: e.target.value
                              }
                            )
                          }
                          placeholder="Proveedor manual"
                          className="w-full border rounded-xl px-3 py-2.5"
                        />

                      )}

                    </div>

                    {/* CANTIDAD / UNIDAD */}
                    <div className="grid grid-cols-2 gap-3">

                      <div>
                        <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
                          Cantidad
                        </label>

                        <input
                          type="number"
                          min="0.01"
                          step="any"
                          value={item.quantity}
                          onChange={e =>
                            updateItem(
                              item.id,
                              {
                                quantity: e.target.value
                              }
                            )
                          }
                          className="w-full border rounded-xl px-3 py-2.5 text-right"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
                          Unidad
                        </label>

                        <input
                          value={item.unit}
                          onChange={e =>
                            updateItem(
                              item.id,
                              {
                                unit: e.target.value
                              }
                            )
                          }
                          className="w-full border rounded-xl px-3 py-2.5"
                        />
                      </div>

                    </div>

                    {/* PRECIO */}
                    <div>

                      <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
                        Precio unitario
                      </label>

                      <div className="relative">

                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">
                          Bs
                        </span>

                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.unit_price}
                          onChange={e =>
                            updateItem(
                              item.id,
                              {
                                unit_price: e.target.value
                              }
                            )
                          }
                          className="w-full border-2 rounded-xl pl-10 pr-3 py-3 text-right font-semibold"
                        />

                      </div>

                      {h?.count > 0 && (

                        <div className="mt-2 flex flex-wrap gap-1.5">

                          <button
                            type="button"
                            onClick={() =>
                              updateItem(
                                item.id,
                                { unit_price: h.last }
                              )
                            }
                            className="px-2.5 py-1.5 text-xs border rounded-lg bg-white hover:bg-slate-50"
                          >
                            Último {money(h.last)}
                          </button>

                          <button
                            type="button"
                            onClick={() =>
                              updateItem(
                                item.id,
                                { unit_price: h.average }
                              )
                            }
                            className="px-2.5 py-1.5 text-xs border rounded-lg bg-white hover:bg-slate-50"
                          >
                            Prom. {money(h.average)}
                          </button>

                          <button
                            type="button"
                            onClick={() =>
                              updateItem(
                                item.id,
                                { unit_price: h.min }
                              )
                            }
                            className="px-2.5 py-1.5 text-xs border rounded-lg bg-white hover:bg-slate-50"
                          >
                            Mín. {money(h.min)}
                          </button>

                        </div>

                      )}

                    </div>

                    {/* HISTORIAL */}
                    {h?.count > 0 && (

                      <div className="history-compact rounded-xl border bg-slate-50 p-2.5">

                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                          Referencia histórica
                        </div>

                        <div className="grid grid-cols-3 gap-2 text-center">

                          <div>
                            <div className="text-[11px] text-slate-500">
                              Último
                            </div>
                            <div className="font-bold text-sm">
                              {money(h.last)}
                            </div>
                          </div>

                          <div>
                            <div className="text-[11px] text-slate-500">
                              Promedio
                            </div>
                            <div className="font-bold text-sm">
                              {money(h.average)}
                            </div>
                          </div>

                          <div>
                            <div className="text-[11px] text-slate-500">
                              Mínimo
                            </div>
                            <div className="font-bold text-sm">
                              {money(h.min)}
                            </div>
                          </div>

                        </div>

                        <div className="text-[11px] text-slate-500 mt-2 text-center">
                          {h.count} compra(s)
                          {h.latestSupplier
                            ? ` · ${h.latestSupplier}`
                            : ''}
                        </div>

                      </div>

                    )}

                  </div>

                </div>

                {/* SUBTOTAL */}
                <div className="mt-4 pt-4 border-t flex items-center justify-between gap-3">

                  <div className="text-xs text-slate-500">
                    {item.source === 'manual'
                      ? 'Producto manual'
                      : 'Producto del catálogo'}
                  </div>

                  <div className="text-right">

                    <div className="text-xs uppercase tracking-wide text-slate-500">
                      Subtotal
                    </div>

                    <div className="text-xl font-bold text-slate-800">
                      {money(subtotal)}
                    </div>

                  </div>

                </div>

              </div>

            </div>
          );
        })}

      </div>

      {/* AGREGAR */}
      <button
        type="button"
        onClick={addItem}
        className="w-full border-2 border-dashed border-slate-300 rounded-2xl py-4 text-slate-600 hover:bg-slate-50 hover:border-slate-400 font-semibold"
      >
        + Agregar otro producto
      </button>

      {/* TOTAL */}
      <div className="sticky-total">

        <div className="sticky-total-inner bg-slate-900 text-white rounded-2xl p-4 sm:p-5">

          <div className="flex items-center justify-between gap-4">

            <div>
              <div className="text-xs uppercase tracking-widest text-slate-300">
                Total proforma
              </div>

              <div className="text-xs text-slate-400 mt-1">
                {quote.items.length} {quote.items.length === 1 ? 'ítem' : 'ítems'}
              </div>
            </div>

            <div className="text-2xl sm:text-3xl font-bold whitespace-nowrap">
              {money(total)}
            </div>

          </div>

          <div className="grid grid-cols-2 gap-2 mt-3">

            <button
              type="button"
              onClick={resetQuote}
              className="rounded-xl bg-white/10 hover:bg-white/20 py-2.5 font-medium"
            >
              Nueva
            </button>

            <button
              type="button"
              onClick={printQuote}
              className="rounded-xl bg-white text-slate-900 hover:bg-slate-100 py-2.5 font-bold"
            >
              🖨 PDF
            </button>

          </div>

        </div>

      </div>

      {loading && (
        <div className="text-sm text-slate-500 text-center py-4">
          Cargando catálogo, historial y pedidos...
        </div>
      )}

    </div>
  );

}
